import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';

export default class ApiSecurityService extends Service {
  @service('error-messages') errorMessages;

  @tracked rateLimiters = new Map();
  @tracked securityEvents = [];
  @tracked blockedIPs = new Set();
  @tracked trustedOrigins = new Set();

  // Rate limiting configurations
  RATE_LIMITS = {
    anonymous: {
      requests: 100,
      windowMs: 15 * 60 * 1000, // 15 minutes
      skipSuccessfulRequests: false,
    },
    authenticated: {
      requests: 1000,
      windowMs: 15 * 60 * 1000, // 15 minutes
      skipSuccessfulRequests: true,
    },
    graphql: {
      requests: 200,
      windowMs: 15 * 60 * 1000, // 15 minutes
      complexityLimit: 1000,
    },
    search: {
      requests: 50,
      windowMs: 10 * 60 * 1000, // 10 minutes
      skipSuccessfulRequests: false,
    },
    admin: {
      requests: 500,
      windowMs: 15 * 60 * 1000, // 15 minutes
      skipSuccessfulRequests: true,
    },
  };

  // Security configuration
  SECURITY_CONFIG = {
    maxRequestSize: 10 * 1024 * 1024, // 10MB
    allowedMethods: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'],
    maxHeaderSize: 8 * 1024, // 8KB
    maxQueryParams: 50,
    maxUrlLength: 2048,
    suspiciousPatterns: [
      /\b(union|select|insert|update|delete|drop|create|alter)\b/i, // SQL injection
      /<script[^>]*>.*?<\/script>/gi, // XSS attempts
      /javascript:/gi, // JavaScript protocol
      /vbscript:/gi, // VBScript protocol
      /on\w+\s*=/gi, // Event handlers
      /expression\s*\(/gi, // CSS expression
      /eval\s*\(/gi, // eval() calls
      /document\.cookie/gi, // Cookie access
      /\.\.\/.*\.\./g, // Directory traversal
    ],
    trustedUserAgents: [
      /googlebot/i,
      /bingbot/i,
      /slackbot/i,
      /twitterbot/i,
      /facebookexternalhit/i,
    ],
  };

  constructor() {
    super(...arguments);
    this.initializeTrustedOrigins();
  }

  /**
   * Initializes trusted origins from environment or configuration
   */
  initializeTrustedOrigins() {
    const trustedOrigins = [
      'https://1vx.gg',
      'https://www.1vx.gg',
      'https://admin.1vx.gg',
    ];

    // Add development origins if in development mode
    if (this.isDevelopment()) {
      trustedOrigins.push(
        'http://localhost:4200',
        'http://localhost:3000',
        'http://127.0.0.1:4200',
        'http://127.0.0.1:3000',
      );
    }

    trustedOrigins.forEach((origin) => this.trustedOrigins.add(origin));
  }

  /**
   * Main security middleware function
   * @param {Object} request - Request object
   * @param {Object} response - Response object
   * @param {Function} next - Next middleware function
   */
  async securityMiddleware(request, response, next) {
    try {
      // Basic security checks
      if (!this.validateBasicSecurity(request)) {
        return this.blockRequest(request, response, 'Invalid request format');
      }

      // Check if IP is blocked
      if (this.isIPBlocked(request.ip)) {
        return this.blockRequest(request, response, 'IP address blocked');
      }

      // CORS validation
      if (!this.validateCORS(request)) {
        return this.blockRequest(request, response, 'CORS policy violation');
      }

      // Rate limiting
      const rateLimitResult = await this.checkRateLimit(request);
      if (!rateLimitResult.allowed) {
        return this.rateLimitExceeded(request, response, rateLimitResult);
      }

      // Content validation
      if (!this.validateRequestContent(request)) {
        return this.blockRequest(
          request,
          response,
          'Malicious content detected',
        );
      }

      // Add security headers to response
      this.addSecurityHeaders(response);

      // Continue to next middleware
      next();
    } catch (error) {
      console.error('Security middleware error:', error);
      this.logSecurityEvent('middleware_error', request, {
        error: error.message,
      });
      return this.blockRequest(request, response, 'Security validation failed');
    }
  }

  /**
   * Validates basic request security requirements
   * @param {Object} request - Request object
   * @returns {boolean} Whether request passes basic validation
   */
  validateBasicSecurity(request) {
    // Check request method
    if (!this.SECURITY_CONFIG.allowedMethods.includes(request.method)) {
      this.logSecurityEvent('invalid_method', request, {
        method: request.method,
      });
      return false;
    }

    // Check URL length
    if (request.url && request.url.length > this.SECURITY_CONFIG.maxUrlLength) {
      this.logSecurityEvent('url_too_long', request, {
        urlLength: request.url.length,
      });
      return false;
    }

    // Check header size
    const headerSize = this.calculateHeaderSize(request.headers);
    if (headerSize > this.SECURITY_CONFIG.maxHeaderSize) {
      this.logSecurityEvent('headers_too_large', request, { headerSize });
      return false;
    }

    // Check query parameters count
    const queryParams = this.getQueryParamsCount(request.url);
    if (queryParams > this.SECURITY_CONFIG.maxQueryParams) {
      this.logSecurityEvent('too_many_params', request, { queryParams });
      return false;
    }

    return true;
  }

  /**
   * Validates CORS policy
   * @param {Object} request - Request object
   * @returns {boolean} Whether request passes CORS validation
   */
  validateCORS(request) {
    const origin = request.headers['origin'];

    // Allow requests without origin (same-origin or direct access)
    if (!origin) {
      return true;
    }

    // Check if origin is trusted
    if (this.trustedOrigins.has(origin)) {
      return true;
    }

    // Allow trusted bots
    const userAgent = request.headers['user-agent'] || '';
    if (this.isTrustedBot(userAgent)) {
      return true;
    }

    this.logSecurityEvent('cors_violation', request, { origin, userAgent });
    return false;
  }

  /**
   * Checks if user agent is a trusted bot
   * @param {string} userAgent - User agent string
   * @returns {boolean} Whether user agent is trusted
   */
  isTrustedBot(userAgent) {
    return this.SECURITY_CONFIG.trustedUserAgents.some((pattern) =>
      pattern.test(userAgent),
    );
  }

  /**
   * Checks rate limits for the request
   * @param {Object} request - Request object
   * @returns {Promise<Object>} Rate limit result
   */
  async checkRateLimit(request) {
    const identifier = this.getRateLimitIdentifier(request);
    const limitType = this.getRateLimitType(request);
    const config = this.RATE_LIMITS[limitType];

    const limiter = this.getRateLimiter(identifier, config);
    const now = Date.now();

    // Clean old requests
    limiter.requests = limiter.requests.filter(
      (timestamp) => now - timestamp < config.windowMs,
    );

    // Check if limit exceeded
    if (limiter.requests.length >= config.requests) {
      this.logSecurityEvent('rate_limit_exceeded', request, {
        identifier,
        limitType,
        requestCount: limiter.requests.length,
      });

      return {
        allowed: false,
        resetTime: limiter.requests[0] + config.windowMs,
        remaining: 0,
        limit: config.requests,
      };
    }

    // Add current request
    limiter.requests.push(now);

    return {
      allowed: true,
      resetTime: now + config.windowMs,
      remaining: config.requests - limiter.requests.length,
      limit: config.requests,
    };
  }

  /**
   * Gets rate limit identifier for the request
   * @param {Object} request - Request object
   * @returns {string} Rate limit identifier
   */
  getRateLimitIdentifier(request) {
    // Use user ID for authenticated requests
    if (request.user && request.user.id) {
      return `user:${request.user.id}`;
    }

    // Use API key if present
    if (request.headers['x-api-key']) {
      return `api-key:${request.headers['x-api-key']}`;
    }

    // Fall back to IP address
    return `ip:${request.ip}`;
  }

  /**
   * Determines rate limit type based on request characteristics
   * @param {Object} request - Request object
   * @returns {string} Rate limit type
   */
  getRateLimitType(request) {
    // Admin endpoints
    if (request.url && request.url.includes('/admin/')) {
      return 'admin';
    }

    // GraphQL endpoint
    if (request.url && request.url.includes('/graphql')) {
      return 'graphql';
    }

    // Search endpoint
    if (request.url && request.url.includes('/search')) {
      return 'search';
    }

    // Authenticated users
    if (request.user || request.headers['authorization']) {
      return 'authenticated';
    }

    // Anonymous users
    return 'anonymous';
  }

  /**
   * Gets or creates rate limiter for identifier
   * @param {string} identifier - Rate limit identifier
   * @param {Object} config - Rate limit configuration
   * @returns {Object} Rate limiter object
   */
  getRateLimiter(identifier, config) {
    if (!this.rateLimiters.has(identifier)) {
      this.rateLimiters.set(identifier, {
        requests: [],
        config,
        createdAt: Date.now(),
      });
    }
    return this.rateLimiters.get(identifier);
  }

  /**
   * Validates request content for malicious patterns
   * @param {Object} request - Request object
   * @returns {boolean} Whether content is safe
   */
  validateRequestContent(request) {
    const contentToCheck = [
      request.url,
      request.query ? JSON.stringify(request.query) : '',
      request.body ? JSON.stringify(request.body) : '',
      request.headers['referer'] || '',
      request.headers['user-agent'] || '',
    ];

    for (const content of contentToCheck) {
      if (this.containsSuspiciousPatterns(content)) {
        this.logSecurityEvent('suspicious_content', request, {
          content: content.substring(0, 200),
        });
        return false;
      }
    }

    return true;
  }

  /**
   * Checks if content contains suspicious patterns
   * @param {string} content - Content to check
   * @returns {boolean} Whether content contains suspicious patterns
   */
  containsSuspiciousPatterns(content) {
    return this.SECURITY_CONFIG.suspiciousPatterns.some((pattern) =>
      pattern.test(content),
    );
  }

  /**
   * Validates GraphQL-specific security requirements
   * @param {Object} request - Request object
   * @param {string} query - GraphQL query
   * @param {Object} variables - GraphQL variables
   * @returns {Object} Validation result
   */
  validateGraphQLSecurity(request, query, variables = {}) {
    const validation = {
      allowed: true,
      reasons: [],
    };

    // Check query complexity
    const complexity = this.estimateQueryComplexity(query, variables);
    if (complexity > this.RATE_LIMITS.graphql.complexityLimit) {
      validation.allowed = false;
      validation.reasons.push('Query complexity exceeds limit');
      this.logSecurityEvent('query_complexity_exceeded', request, {
        complexity,
      });
    }

    // Check for introspection queries in production
    if (!this.isDevelopment() && this.isIntrospectionQuery(query)) {
      validation.allowed = false;
      validation.reasons.push(
        'Introspection queries not allowed in production',
      );
      this.logSecurityEvent('introspection_blocked', request);
    }

    // Check for potentially dangerous mutations
    if (this.isDangerousMutation(query)) {
      validation.allowed = false;
      validation.reasons.push('Dangerous mutation detected');
      this.logSecurityEvent('dangerous_mutation', request, {
        query: query.substring(0, 200),
      });
    }

    return validation;
  }

  /**
   * Estimates GraphQL query complexity
   * @param {string} query - GraphQL query
   * @param {Object} variables - Query variables
   * @returns {number} Estimated complexity
   */
  estimateQueryComplexity(query, variables) {
    // Simple complexity estimation
    let complexity = 0;

    // Count field selections
    const fieldMatches = query.match(/\w+\s*[\{\(]/g) || [];
    complexity += fieldMatches.length * 10;

    // Count collection queries
    const collectionMatches = query.match(/Collection\s*\(/g) || [];
    complexity += collectionMatches.length * 50;

    // Factor in variable limits
    if (variables.limit && variables.limit > 50) {
      complexity += variables.limit * 2;
    }

    return complexity;
  }

  /**
   * Checks if query is an introspection query
   * @param {string} query - GraphQL query
   * @returns {boolean} Whether query is introspection
   */
  isIntrospectionQuery(query) {
    return query.includes('__schema') || query.includes('__type');
  }

  /**
   * Checks if mutation is potentially dangerous
   * @param {string} query - GraphQL query
   * @returns {boolean} Whether mutation is dangerous
   */
  isDangerousMutation(query) {
    const dangerousPatterns = [
      /mutation.*delete.*all/i,
      /mutation.*drop/i,
      /mutation.*truncate/i,
      /mutation.*admin.*delete/i,
    ];

    return dangerousPatterns.some((pattern) => pattern.test(query));
  }

  /**
   * Adds security headers to response
   * @param {Object} response - Response object
   */
  addSecurityHeaders(response) {
    const headers = {
      'X-Content-Type-Options': 'nosniff',
      'X-Frame-Options': 'DENY',
      'X-XSS-Protection': '1; mode=block',
      'Referrer-Policy': 'strict-origin-when-cross-origin',
      'Content-Security-Policy': this.getCSPHeader(),
      'Strict-Transport-Security': 'max-age=31536000; includeSubDomains',
      'Permissions-Policy': this.getPermissionsPolicyHeader(),
    };

    Object.entries(headers).forEach(([name, value]) => {
      response.setHeader(name, value);
    });
  }

  /**
   * Generates Content Security Policy header
   * @returns {string} CSP header value
   */
  getCSPHeader() {
    const directives = [
      "default-src 'self'",
      "script-src 'self' 'unsafe-inline' 'unsafe-eval' https://www.googletagmanager.com https://www.google-analytics.com",
      "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
      "font-src 'self' https://fonts.gstatic.com",
      "img-src 'self' data: https: http:",
      "connect-src 'self' https://api.contentful.com https://graphql.contentful.com",
      "media-src 'self' https:",
      "frame-src 'self' https://www.youtube.com https://player.twitch.tv",
      "frame-ancestors 'self'",
      "worker-src 'self' blob:",
    ];

    return directives.join('; ');
  }

  /**
   * Generates Permissions Policy header
   * @returns {string} Permissions Policy header value
   */
  getPermissionsPolicyHeader() {
    const policies = [
      'geolocation=()',
      'microphone=()',
      'camera=()',
      'payment=()',
      'usb=()',
      'magnetometer=()',
      'gyroscope=()',
      'accelerometer=()',
    ];

    return policies.join(', ');
  }

  /**
   * Blocks a request and sends error response
   * @param {Object} request - Request object
   * @param {Object} response - Response object
   * @param {string} reason - Block reason
   */
  blockRequest(request, response, reason) {
    this.logSecurityEvent('request_blocked', request, { reason });

    response.status(403).json({
      error: 'Request blocked for security reasons',
      message:
        'Your request has been blocked due to security policy violations.',
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Handles rate limit exceeded responses
   * @param {Object} request - Request object
   * @param {Object} response - Response object
   * @param {Object} rateLimitResult - Rate limit result
   */
  rateLimitExceeded(request, response, rateLimitResult) {
    response.status(429).json({
      error: 'Rate limit exceeded',
      message: 'Too many requests. Please try again later.',
      retryAfter: Math.ceil((rateLimitResult.resetTime - Date.now()) / 1000),
      limit: rateLimitResult.limit,
      remaining: rateLimitResult.remaining,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Checks if IP address is blocked
   * @param {string} ip - IP address
   * @returns {boolean} Whether IP is blocked
   */
  isIPBlocked(ip) {
    return this.blockedIPs.has(ip);
  }

  /**
   * Blocks an IP address
   * @param {string} ip - IP address to block
   * @param {string} reason - Block reason
   * @param {number} duration - Block duration in ms (0 = permanent)
   */
  blockIP(ip, reason, duration = 0) {
    this.blockedIPs.add(ip);

    this.logSecurityEvent('ip_blocked', null, { ip, reason, duration });

    // Automatically unblock after duration
    if (duration > 0) {
      setTimeout(() => {
        this.blockedIPs.delete(ip);
        this.logSecurityEvent('ip_unblocked', null, { ip, reason: 'timeout' });
      }, duration);
    }
  }

  /**
   * Logs security events for monitoring
   * @param {string} type - Event type
   * @param {Object} request - Request object
   * @param {Object} metadata - Additional metadata
   */
  logSecurityEvent(type, request, metadata = {}) {
    const event = {
      type,
      timestamp: new Date().toISOString(),
      ip: request?.ip,
      userAgent: request?.headers['user-agent'],
      url: request?.url,
      method: request?.method,
      ...metadata,
    };

    this.securityEvents.push(event);

    // Keep only last 1000 events in memory
    if (this.securityEvents.length > 1000) {
      this.securityEvents.shift();
    }

    // Log to console in development
    if (this.isDevelopment()) {
      console.warn('Security Event:', event);
    }
  }

  /**
   * Helper functions
   */

  calculateHeaderSize(headers) {
    return JSON.stringify(headers).length;
  }

  getQueryParamsCount(url) {
    if (!url) return 0;
    const queryString = url.split('?')[1];
    if (!queryString) return 0;
    return queryString.split('&').length;
  }

  isDevelopment() {
    return (
      typeof process !== 'undefined' && process.env?.NODE_ENV === 'development'
    );
  }

  /**
   * Gets security statistics
   * @returns {Object} Security stats
   */
  getSecurityStats() {
    const recentEvents = this.securityEvents.filter(
      (event) =>
        Date.now() - new Date(event.timestamp).getTime() < 24 * 60 * 60 * 1000,
    );

    const eventsByType = recentEvents.reduce((acc, event) => {
      acc[event.type] = (acc[event.type] || 0) + 1;
      return acc;
    }, {});

    return {
      totalEvents: this.securityEvents.length,
      recentEvents: recentEvents.length,
      eventsByType,
      blockedIPs: this.blockedIPs.size,
      activeLimiters: this.rateLimiters.size,
      trustedOrigins: Array.from(this.trustedOrigins),
    };
  }

  /**
   * Cleans up old data to prevent memory leaks
   */
  cleanup() {
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;

    // Clean old rate limiters
    for (const [key, limiter] of this.rateLimiters.entries()) {
      if (now - limiter.createdAt > oneHour && limiter.requests.length === 0) {
        this.rateLimiters.delete(key);
      }
    }

    // Clean old security events
    this.securityEvents = this.securityEvents.filter(
      (event) =>
        now - new Date(event.timestamp).getTime() < 7 * 24 * 60 * 60 * 1000, // 7 days
    );
  }
}
